import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { rhm, apiError, dOnly } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Card, Empty, Input, PageHead, Table, Td } from "../ui";

export default function RhmReorders() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ["rhm-reorders"],
        queryFn: async () => (await rhm.get("/reorders")).data,
    });

    const patch = useMutation({
        mutationFn: async ({ id, payload }) => (await rhm.patch(`/reorders/${id}`, payload)).data,
        onSuccess: () => { toast.success("Reorder updated"); qc.invalidateQueries({ queryKey: ["rhm-reorders"] }); },
        onError: (e) => toast.error(apiError(e)),
    });

    const Section = ({ title, rows, tone }) => (
        <Card title={`${title} (${rows.length})`} className="mb-5">
            <Table head={["Customer", "Product", "Delivered", "Expected", "State", "Reminders", "Actions"]}
                empty={!rows.length && <Empty msg="Nothing here." />}>
                {rows.map((r) => (
                    <tr key={r.id} data-testid={`rhm-reorder-row-${r.id}`}>
                        <Td>
                            <div className="font-semibold">{r.customer_name || "—"}</div>
                            <div className="text-xs text-zinc-500">{r.customer_email}</div>
                        </Td>
                        <Td className="text-sm">{r.product_name}</Td>
                        <Td className="text-xs text-zinc-500">{dOnly(r.delivered_at)}</Td>
                        <Td>
                            {can("customers.edit") ? (
                                <Input type="date" className="!w-40 !py-1.5" defaultValue={(r.expected_date || "").slice(0, 10)}
                                    data-testid={`reorder-date-${r.id}`}
                                    onBlur={(e) => e.target.value !== (r.expected_date || "").slice(0, 10)
                                        && patch.mutate({ id: r.id, payload: { expected_date: e.target.value } })} />
                            ) : dOnly(r.expected_date)}
                        </Td>
                        <Td><Badge tone={tone}>{String(r.state).toUpperCase()}</Badge></Td>
                        <Td className="text-sm text-zinc-400">{r.reminders_sent || 0}</Td>
                        <Td>
                            {can("customers.edit") && (
                                <div className="flex gap-3">
                                    <button onClick={() => patch.mutate({ id: r.id, payload: { status: "converted" } })}
                                        data-testid={`reorder-convert-${r.id}`}
                                        className="mono text-[10px] tracking-[0.16em] text-lime">CONVERTED</button>
                                    <button onClick={() => patch.mutate({ id: r.id, payload: { status: "dismissed" } })}
                                        className="mono text-[10px] tracking-[0.16em] text-zinc-500">DISMISS</button>
                                </div>
                            )}
                        </Td>
                    </tr>
                ))}
            </Table>
        </Card>
    );

    if (isLoading) return <div className="mono text-xs text-zinc-500">LOADING REORDERS…</div>;

    return (
        <div data-testid="rhm-reorders-page">
            <PageHead title="Customer Reorders"
                sub="Predicted from each product's consumption period once an order is delivered." />
            <Section title="Overdue Reorders" rows={data?.overdue || []} tone="danger" />
            <Section title="Upcoming Reorders" rows={data?.upcoming || []} tone="gold" />
            <Section title="Closed" rows={data?.closed || []} tone="muted" />
        </div>
    );
}
